import { type QueryKey, skipToken, useQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Agents } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { CLIPS_LIST_BY_IDS_MAX_IDS } from '~/components/v2/components/workspace/workspace-agent-panel/utils/chunkWorkspaceAgentClipIds';
import { isWorkspaceAgentSessionUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionUnresolvableError';

export const PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACT_ROWS_KEY = 'PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACT_ROWS';
/**
 * One page, sized to `Clips.listByIds`'s id cap so the follow-up lookup for this artifact is a
 * single request. `pagination.hasMore` means the artifact is longer than this.
 */
const ARTIFACT_ROW_PAGE_LIMIT = CLIPS_LIST_BY_IDS_MAX_IDS;
const ARTIFACT_ROW_TRANSIENT_RETRIES = 1;

export type PrivateWorkspaceAgentSessionArtifactRowsParams = Omit<
  NonNullable<Parameters<typeof Agents.Sessions.Artifacts.Rows.list>[0]>,
  'agentType' | 'artifactId' | 'cursor' | 'limit' | 'sessionId' | 'workspaceId'
> & {
  artifactId: string | undefined;
  sessionId: string | undefined;
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentSessionArtifactRowsKey = [
  typeof PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACT_ROWS_KEY,
  PrivateWorkspaceAgentSessionArtifactRowsParams,
];

export const getPrivateWorkspaceAgentSessionArtifactRowsKey = (
  params: PrivateWorkspaceAgentSessionArtifactRowsParams,
): PrivateWorkspaceAgentSessionArtifactRowsKey => [PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACT_ROWS_KEY, params];

export const isPrivateWorkspaceAgentSessionArtifactRowsKey = (
  key: QueryKey,
): key is PrivateWorkspaceAgentSessionArtifactRowsKey =>
  Array.isArray(key) && key[0] === PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACT_ROWS_KEY && isObject(key[1]);

export type UsePrivateWorkspaceAgentSessionArtifactRowsOptions = {
  enabled?: boolean;
};

export const usePrivateWorkspaceAgentSessionArtifactRows = (
  params: PrivateWorkspaceAgentSessionArtifactRowsParams,
  options?: UsePrivateWorkspaceAgentSessionArtifactRowsOptions,
) => {
  const { artifactId, sessionId, workspaceId } = params;
  const canFetch = Boolean(workspaceId) && Boolean(sessionId) && Boolean(artifactId);

  return useQuery({
    enabled: canFetch && (options?.enabled ?? true),
    queryFn:
      workspaceId && sessionId && artifactId
        ? () =>
            callAirApi(() =>
              Agents.Sessions.Artifacts.Rows.list({
                agentType: 'workspace',
                artifactId,
                limit: ARTIFACT_ROW_PAGE_LIMIT,
                sessionId,
                workspaceId,
              }),
            )
        : skipToken,
    queryKey: getPrivateWorkspaceAgentSessionArtifactRowsKey(params),
    retry: (failureCount, error) =>
      !isWorkspaceAgentSessionUnresolvableError(error) && failureCount < ARTIFACT_ROW_TRANSIENT_RETRIES,
  });
};
